import { sortBy } from 'lodash';
import React, { useEffect } from 'react';

import { useAdminContext } from 'App/pages/Admin/AdminContext';
import 'App/upsales/common/components/react/admin/sidebar';
import { useSelector, useForceRender } from 'App/components/hooks';
import i18next from 'i18next';
import history from 'App/pages/routes/history';
import useAdminRoutes from '../allAdminRoutes';
import type { match } from 'react-router-dom';

import './AdminSidebar.scss';

// Exported for reuse in angular controller
export const reactRouteMap = {
	ticketRules: '/admin/customer-support/rules',
	ticketForms: '/admin/customer-support/forms',
	customerSupport: '/admin/customer-support/verify',
	projectplan: '/admin/projectplan',
	aiPrompts: '/admin/ai-prompts',
	projectPlanTemplates: '/admin/project-plan/templates',
	projectPlanFields: '/admin/project-plan/fields',
	projectPlanWorkProcess: '/admin/project-plan/workprocess',
	projectPlanPriorityAndStatus: '/admin/project-plan/priorityandstatus',
	ticketFields: '/admin/customer-support/fields',
	ticketResponseTemplates: '/admin/customer-support/responsetemplates',
	ticketAutoReplyTemplates: '/admin/customer-support/autoreplytemplates',
	creditMonitoring: '/admin/credit-monitoring',
	profile: '/admin/profile',
	notificationCenter: '/admin/notifications',
	subscriptionIndexing: '/admin/subscriptionIndexing',
	activityTypes: '/admin/activitytypes',
	defaultMailSignature: '/admin/email-signatures',
	optIn: '/admin/optin',
	optOut: '/admin/optOut',
	phonecallgoals: '/admin/phonecall-goals',
	salesboards: '/admin/salesboards',
	accountsettings: '/admin/accountsettings',
	completedappointmentgoals: '/admin/completed-appointment-goals',
	bookedappointmentgoals: '/admin/booked-appointment-goals',
	reminders: '/admin/reminders',
	mailSignature: '/admin/mailSignature',
	productquota: '/admin/product-quota'
} as const;

const AdminSidebar = ({ match }: { match: match<Record<string, string>> }) => {
	const { metadata, self } = useSelector(({ App }) => App);
	const { forced, triggerRender } = useForceRender();
	const routes = useAdminRoutes();

	const currentRoute = routes.find(route => route.path === match.path);
	const { sidebarSearch } = useAdminContext();

	useEffect(() => {
		i18next.on('languageChanged', triggerRender);
		return () => {
			i18next.off('languageChanged', triggerRender);
		};
	}, []);

	var isSelectedState = function (stateName: string, params?: any, opts?: any) {
		if (!currentRoute) {
			return false;
		}
		const currentRouteState = `administration.${currentRoute.oldRouteState}`;
		const correctRoute = stateName === currentRouteState;
		if ((opts && opts.ignoreParams) || !params) {
			return correctRoute;
		}

		let validParams = true;
		Object.keys(params).forEach(key => {
			if (params[key] !== match.params[key]) {
				validParams = false;
			}
		});
		return stateName === currentRouteState && validParams;
	};

	const setPage = (state: string, params: any) => {
		if (state in reactRouteMap) {
			history.push(reactRouteMap[state as keyof typeof reactRouteMap]);
		} else if (!isSelectedState('administration.' + state, params)) {
			Tools.$state.go('administration.' + state, params);
		}
	};

	return (
		<ReactTemplates.admin.sidebar
			key={`admin-sidebar-${forced}`}
			rootData={{
				state: currentRoute?.oldRouteState ?? '',
				setPage: setPage,
				isSelectedState: isSelectedState,
				currentUrl: '',
				integrations: {
					userConfigurable: sortBy(metadata?.integrations.userConfigurable ?? [], 'name'),
					global: sortBy(metadata?.integrations.active ?? [], 'name').filter(
						integration => !integration.userOnly
					),
					mainApps: metadata?.mainApps ?? []
				},
				outsideSearch: sidebarSearch,
				self
			}}
		/>
	);
};

export default AdminSidebar;
